/**
 * The creator's multicam cut as an edit an editing app opens directly, so the
 * editor relinks media instead of rebuilding every cut from markers.
 *
 *  - `buildPremiereXml`: Final Cut Pro 7 XML ("xmeml"), which Premiere Pro
 *    (File > Import) and DaVinci Resolve (File > Import > Timeline) read.
 *  - `buildFcpxml`: FCPXML 1.10 with a real multicam clip, for Final Cut Pro
 *    (File > Import > XML) and DaVinci Resolve 18 or later.
 *
 * Both are written from one plan (`planRoughCut`), so they agree frame for
 * frame.
 *
 * Timeline model, as in multicamReview and multicamStudioModel:
 *  - timeline time is the master angle's clock;
 *  - an angle's source position is timelineMs - timelineStartMs + sourceInMs,
 *    with footage from sourceInMs up to sourceOutMs (or the file's length);
 *  - the program shows the master until the first SWITCH, then each SWITCH's
 *    target from the frame it is on.
 *
 * Frames:
 *  - Rates are written exactly: 29.97 is 30000/1001 (timebase 30 with NTSC in
 *    xmeml, a 1001/30000s frame in FCPXML).
 *  - A moment on the timeline (a cut, a note) belongs to the frame it falls
 *    in, as in timecode.ts, so it lands on the frame PithPlay labels it with.
 *  - Sync offsets and trims (timelineStartMs, sourceInMs, sourceOutMs) are
 *    snapped to the nearest frame here, once; both exports use the result.
 *  - Lengths (durationMs, the timeline length) count whole frames.
 *
 * Rules for awkward data:
 *  - Cuts at or after the end of the timeline are ignored. Several cuts on one
 *    frame: the last one wins, as in the multicam studio. Neighbouring stretches
 *    of the same angle merge into one clip.
 *  - Where the angle the cut calls for has no footage (or is not in the set),
 *    the rough cut shows the master; if the master has none either, the first
 *    angle (A, B, C) that does; if none has footage, nothing (black). Each such
 *    stretch gets a marker saying what the cut asked for.
 *  - An angle whose length was not recorded runs to the end of the timeline.
 *  - Negative offsets are treated as 0, as normalizeAngles does for legacy sets.
 *  - The sound comes from the set's separate recording when it has one
 *    (placed by its own offset and trim, like an angle), otherwise from the
 *    audio angle (the master when it is missing), wherever it has sound.
 *  - Notes past the end are pinned to the last frame. Range notes keep their
 *    end in xmeml; FCPXML markers are one frame long (Final Cut's rule), so
 *    the end goes into the marker's note.
 *
 * Relinking: neither export can point at the editor's copy of the media, so
 * each file is referenced by name. xmeml uses file://localhost/<name>, which
 * no app finds, so Premiere and Resolve ask for the media and match by file
 * name. FCPXML uses ./<name>, a URL relative to the .fcpxml (Apple's
 * media-rep rules), so Final Cut links files saved next to it on import.
 * Remote URLs are never written: the in-app links carry a non-expiring access
 * token that must not travel in a file people share.
 *
 * Source in/out frames count from the first frame of each file. Files that
 * carry their own start timecode can conform offset in apps that match by
 * timecode; the snapshot does not record a file's start timecode.
 */

import { directionCategoryOf, getAnnotationCategory } from './annotationCategories';
import { safeFileName } from './annotationExport';
import { framesToTimecode, isDropFrameRate, nominalFps, normalizeFps, secondsToFrames } from './timecode';
import { parseMarkText } from './transcriptMarks';

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/** One camera angle, as stored on the multicam snapshot. */
export interface NleAngle {
  angleKey: string;
  label?: string | null;
  /** The uploaded file's name, such as "cam2.mp4". */
  videoName?: string | null;
  videoUrl?: string | null;
  /** 0 or missing: not recorded. */
  durationMs?: number | null;
  timelineStartMs?: number | null;
  sourceInMs?: number | null;
  /** null: to the end of the file. */
  sourceOutMs?: number | null;
}

/** A multicam annotation (the shape of ReviewAnnotation in multicamReview). */
export interface NleAnnotation {
  timelineTimeMs: number;
  endTimeMs?: number | null;
  type: string;
  targetAngleKey?: string | null;
  text?: string | null;
  category?: string | null;
  color?: string | null;
}

/** A separate sound recording (a recorder's WAV, MP3, M4A, AAC or AIFF), timed like an angle. */
export interface NleAudioSource {
  label?: string | null;
  /** The uploaded file's name, such as "ZOOM0001.WAV". */
  videoName?: string | null;
  videoUrl?: string | null;
  durationMs?: number | null;
  timelineStartMs?: number | null;
  sourceInMs?: number | null;
  sourceOutMs?: number | null;
  /** 1 for mono; unknown means stereo. */
  channels?: number | null;
  /** Unknown means 48 kHz. */
  sampleRate?: number | null;
}

/** The key the recording goes by among the angles' file names. */
export const AUDIO_SOURCE_KEY = 'AUDIO';

export interface RoughCutInput {
  /** The project name: names the sequence, the event and the downloaded file. */
  title: string;
  angles: NleAngle[];
  annotations: NleAnnotation[];
  audioAngleKey?: string | null;
  /** The set's separate sound recording: when given, the cut's sound comes from it. */
  audioSource?: NleAudioSource | null;
  /** The set's timeline length; 0 or missing: the end of the last angle of known length. */
  timelineDurationMs?: number | null;
  /** Frame rate; unknown or invalid means 30. */
  fps?: number | null;
  /** Frame size. The snapshot does not record it, so 1920×1080 by default. */
  width?: number | null;
  height?: number | null;
}

// ---------------------------------------------------------------------------
// Frame rate
// ---------------------------------------------------------------------------

export interface NleRate {
  /** Whole frames per timecode second: the xmeml <timebase>. */
  timebase: number;
  /** The 1000/1001 NTSC rate: the xmeml <ntsc>. */
  ntsc: boolean;
  /** Exact frames per second: 30000/1001 for 29.97. */
  fps: number;
  /** One frame in seconds as numerator/denominator, the way Final Cut writes it: 1001/30000, 100/3000. */
  frameNumerator: number;
  frameDenominator: number;
  /** Drop-frame timecode labels (29.97 and 59.94, as in timecode.ts). */
  dropFrame: boolean;
}

/** Within this of 24000/1001, 30000/1001 and so on, a rate is the NTSC one ("23.98" included). */
const NTSC_TOLERANCE = 0.005;

/**
 * The exact rate a file is written at. Rates that are neither whole nor NTSC
 * (12.5) are rounded to the nearest whole rate, the only other kind xmeml can
 * express; FCPXML uses the same rate so the two exports agree.
 */
export const nleRate = (fps?: number | null): NleRate => {
  const requested = normalizeFps(fps);
  const timebase = nominalFps(requested);
  const ntscFps = (timebase * 1000) / 1001;
  const ntsc = Math.abs(requested - timebase) > NTSC_TOLERANCE && Math.abs(requested - ntscFps) <= NTSC_TOLERANCE;
  return {
    timebase,
    ntsc,
    fps: ntsc ? ntscFps : timebase,
    frameNumerator: ntsc ? 1001 : 100,
    frameDenominator: ntsc ? timebase * 1000 : timebase * 100,
    dropFrame: ntsc && isDropFrameRate(requested),
  };
};

const finite = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** The frame a moment on the timeline falls in (floors, like secondsToFrames). */
const frameAt = (ms: number, rate: NleRate) => secondsToFrames(Math.max(0, ms) / 1000, rate.fps);

/** Whole frames in a length. */
const wholeFrames = (ms: number, rate: NleRate) => secondsToFrames(Math.max(0, ms) / 1000, rate.fps);

/**
 * A sync offset or trim, snapped to the nearest frame. Offsets are set by
 * ear or with a slider in hundredths of a second, so they rarely sit on a
 * frame boundary; the nearest frame is the best sync either app can show.
 */
export const snapOffsetToFrames = (ms: number, rate: NleRate): number =>
  Math.max(0, Math.round((Math.max(0, finite(ms)) / 1000) * rate.fps));

// ---------------------------------------------------------------------------
// Text that is safe in XML, in names and on disk
// ---------------------------------------------------------------------------

/** Drops what XML 1.0 cannot carry: control characters, lone surrogates, U+FFFE/U+FFFF. */
const xmlChars = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += text.charAt(i) + text.charAt(i + 1);
        i += 1;
      }
      continue;
    }
    if (code >= 0xdc00 && code <= 0xdfff) continue;
    if (code === 0x9 || code === 0xa || code === 0xd || (code >= 0x20 && code !== 0xfffe && code !== 0xffff)) {
      out += text.charAt(i);
    }
  }
  return out;
};

const escapeText = (value: unknown): string =>
  xmlChars(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

/** Attribute values also encode line breaks and tabs, which parsers would otherwise turn into spaces. */
const escapeAttribute = (value: unknown): string =>
  escapeText(value).replace(/\r/g, '&#13;').replace(/\n/g, '&#10;').replace(/\t/g, '&#9;');

/** One line, trimmed and capped, for names. */
const oneLine = (value: unknown, max = 120): string => {
  const line = xmlChars(value).replace(/\s+/g, ' ').trim();
  // Re-run xmlChars so a cut through an emoji cannot leave half of it behind.
  return line.length > max ? `${xmlChars(line.slice(0, max - 1)).trim()}…` : line;
};

/** Note text: line breaks kept, the rest of the whitespace left alone. */
const noteText = (value: unknown): string => xmlChars(value).replace(/\r\n?/g, '\n').trim();

/** "B · Close-up", or "Angle B" (the same rule as angleName in multicamStudioModel). */
const angleDisplayName = (angle: { angleKey: string; label?: string | null }): string => {
  const label = oneLine(angle.label ?? '', 60);
  return label && label.toLowerCase() !== `angle ${angle.angleKey}`.toLowerCase()
    ? `${angle.angleKey} · ${label}`
    : `Angle ${angle.angleKey}`;
};

/** "Separate audio (Zoom H6)", or "Separate audio". */
const recordingName = (source: NleAudioSource | null | undefined): string => {
  const label = oneLine(source?.label ?? '', 60);
  return label && label.toLowerCase() !== 'separate audio' ? `Separate audio (${label})` : 'Separate audio';
};

const EXTENSION = /\.[A-Za-z0-9]{1,5}$/;
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com\d|lpt\d)$/i;

const extensionOf = (name: string): string => {
  const match = name.match(EXTENSION);
  return match ? match[0] : '';
};

const urlPath = (url?: string | null): string => String(url ?? '').split(/[?#]/)[0].trim();

const urlFileName = (url?: string | null): string => {
  const last = urlPath(url).split('/').filter(Boolean).pop() || '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

/**
 * A file name every OS accepts, kept as close to the uploaded name as
 * possible: the editor's copy must match it exactly for apps to relink by name.
 */
const cleanFileName = (angle: NleAngle, fallbackExtension = '.mp4', fallbackBase = `angle-${angle.angleKey}`): string => {
  const raw = (xmlChars(angle.videoName ?? '').trim().split(/[\\/]/).pop() || '').trim();
  let extension = extensionOf(raw);
  let base = extension ? raw.slice(0, raw.length - extension.length) : raw;
  // A library name without an extension ("Podcast cam 2"): take it from the upload.
  if (!extension) extension = extensionOf(urlFileName(angle.videoUrl)) || fallbackExtension;
  // Characters Windows rejects, and a leading dot (a hidden file). The name
  // always ends in its extension, so it never ends in a dot or a space.
  base = base
    .replace(/[<>:"|?*\t\n\r]/g, '_')
    .replace(/^[.\s]+/, '')
    .slice(0, 150);
  if (!base.trim()) base = fallbackBase;
  // CON, NUL, COM1 and the rest are reserved on Windows, extension or not.
  if (WINDOWS_RESERVED.test(base.trim())) base = `_${base}`;
  return `${base}${extension}`;
};

/** Angles that use the same file (same URL) share it; otherwise each angle is its own file. */
const mediaIdentity = (angle: NleAngle): string => {
  const path = urlPath(angle.videoUrl);
  return path ? `url:${path}` : `angle:${angle.angleKey}`;
};

const dictionary = <T>(): Record<string, T> => Object.create(null);

/** Valid angles, one per key (the first wins), in key order like normalizeAngles. */
const usableAngles = (angles: NleAngle[] | null | undefined): NleAngle[] => {
  const seen = dictionary<boolean>();
  return (Array.isArray(angles) ? angles : [])
    .filter((angle): angle is NleAngle => {
      if (!angle || typeof angle.angleKey !== 'string' || angle.angleKey.trim() === '') return false;
      if (seen[angle.angleKey]) return false;
      seen[angle.angleKey] = true;
      return true;
    })
    .sort((a, b) => a.angleKey.localeCompare(b.angleKey));
};

/** The recording as an angle-shaped entry, or null when there is none to place. */
const recordingAsAngle = (source: NleAudioSource | null | undefined): NleAngle | null =>
  source && (source.videoUrl || source.videoName) ? { ...source, angleKey: AUDIO_SOURCE_KEY } : null;

/**
 * The file name each angle's media is expected to have on the editor's disk,
 * by angle key. Usually the uploaded name; two different files with the same
 * name (two cameras that both write C0001.MP4) are told apart by angle, since
 * one folder cannot hold both. The page shows these names next to each angle.
 * The separate recording, when given, is named under AUDIO_SOURCE_KEY.
 */
export const angleFileNames = (angles: NleAngle[], audioSource?: NleAudioSource | null): Record<string, string> => {
  const byIdentity = dictionary<string>();
  const taken = dictionary<boolean>();
  const names = dictionary<string>();
  const recording = recordingAsAngle(audioSource);
  usableAngles(angles).concat(recording ? [recording] : []).forEach((angle) => {
    const identity = mediaIdentity(angle);
    if (byIdentity[identity]) {
      names[angle.angleKey] = byIdentity[identity];
      return;
    }
    const wanted = angle === recording ? cleanFileName(angle, '.wav', 'separate-audio') : cleanFileName(angle);
    const extension = extensionOf(wanted);
    const base = wanted.slice(0, wanted.length - extension.length);
    let name = wanted;
    // Case-insensitive: macOS and Windows treat CAM.mp4 and cam.mp4 as one file.
    for (let attempt = 1; taken[name.toLowerCase()]; attempt += 1) {
      name = `${base} (${angle.angleKey}${attempt > 1 ? `-${attempt}` : ''})${extension}`;
    }
    taken[name.toLowerCase()] = true;
    byIdentity[identity] = name;
    names[angle.angleKey] = name;
  });
  return names;
};

// ---------------------------------------------------------------------------
// The plan: angles, the program and markers, in frames
// ---------------------------------------------------------------------------

export interface FrameRange {
  start: number;
  /** Exclusive. */
  end: number;
}

export interface PlannedFile {
  /** Unique id within an export. */
  id: string;
  /**
   * The name the editor's copy is expected to have (see angleFileNames): the
   * uploaded name unless it had to be made safe or told apart from another.
   */
  fileName: string;
  /** Frames the file is declared to hold: its recorded length, and never less than the cut uses. */
  mediaFrames: number;
  /** A sound recording, with no picture. */
  audioOnly?: boolean;
  /** Sound channels (2 when unknown) and sample rate (48 kHz when unknown). */
  channels?: number;
  sampleRate?: number;
}

export interface PlannedAngle {
  angleKey: string;
  /** "B · Close-up", or "Angle B". */
  name: string;
  file: PlannedFile;
  /** Timeline frame where the first usable source frame sits: the sync offset. */
  startFrame: number;
  /** First usable source frame. */
  inFrame: number;
  /** Timeline frames with footage, within the cut; null when there are none. */
  coverage: FrameRange | null;
  /** False when neither the file's length nor an out point was recorded. */
  lengthKnown: boolean;
}

export interface ProgramSegment extends FrameRange {
  /** The angle shown, or null where no angle has footage. */
  angleKey: string | null;
}

/** 'remove' and 'short': the creator's directions from the transcript, as range markers of their own. */
export type MarkerKind = 'note' | 'cut' | 'fallback' | 'remove' | 'short';

export interface PlannedMarker {
  kind: MarkerKind;
  frame: number;
  /** Exclusive end of a range marker; null for a point. */
  endFrame: number | null;
  name: string;
  comment: string;
}

export interface RoughCutPlan {
  title: string;
  rate: NleRate;
  width: number;
  height: number;
  /** Length of the cut. */
  totalFrames: number;
  masterKey: string;
  /** The angle the sound comes from when there is no separate recording. */
  audioKey: string;
  /** The separate recording, placed like an angle (angleKey AUDIO_SOURCE_KEY); the sound comes from it. */
  audio: PlannedAngle | null;
  files: PlannedFile[];
  /** In key order. */
  angles: PlannedAngle[];
  /** The program: contiguous, from 0 to totalFrames. */
  segments: ProgramSegment[];
  /** In time order. */
  markers: PlannedMarker[];
}

export type RoughCutProblem = 'no-angles' | 'no-length';

const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;

const isSwitch = (annotation: NleAnnotation) => String(annotation.type || '').toUpperCase() === 'SWITCH';

/** The usable end of an angle's source, in ms, or null when unknown (sourceEndMs in multicamReview). */
const sourceEndMs = (angle: NleAngle): number | null => {
  const sourceIn = Math.max(0, finite(angle.sourceInMs));
  if (positive(angle.sourceOutMs) && angle.sourceOutMs > sourceIn) return angle.sourceOutMs;
  return positive(angle.durationMs) ? angle.durationMs : null;
};

/** The cut's length: the set's recorded length, else the end of the last angle of known length. */
const timelineFrames = (input: RoughCutInput, angles: NleAngle[], rate: NleRate): number => {
  if (positive(input.timelineDurationMs)) return wholeFrames(input.timelineDurationMs, rate);
  const endMs = angles.reduce((max, angle) => {
    const end = sourceEndMs(angle);
    if (end === null) return max;
    const start = Math.max(0, finite(angle.timelineStartMs));
    return Math.max(max, start + Math.max(0, end - Math.max(0, finite(angle.sourceInMs))));
  }, 0);
  return wholeFrames(endMs, rate);
};

/** Why the rough cut cannot be built, or null when it can. */
export const roughCutProblem = (input: RoughCutInput): RoughCutProblem | null => {
  const angles = usableAngles(input.angles);
  if (angles.length === 0) return 'no-angles';
  return timelineFrames(input, angles, nleRate(input.fps)) > 0 ? null : 'no-length';
};

const planAngle = (angle: NleAngle, file: PlannedFile, rate: NleRate, totalFrames: number): PlannedAngle => {
  // Legacy sets stored negative offsets; the studio treats them as 0, so does this.
  const startFrame = snapOffsetToFrames(finite(angle.timelineStartMs), rate);
  const inFrame = snapOffsetToFrames(finite(angle.sourceInMs), rate);
  const recordedFrames = positive(angle.durationMs) ? wholeFrames(angle.durationMs, rate) : null;
  const sourceInMs = Math.max(0, finite(angle.sourceInMs));
  let sourceEnd: number | null = recordedFrames;
  if (positive(angle.sourceOutMs) && angle.sourceOutMs > sourceInMs) {
    const outFrame = snapOffsetToFrames(angle.sourceOutMs, rate);
    sourceEnd = recordedFrames === null ? outFrame : Math.min(outFrame, recordedFrames);
  }
  // Unknown length: assume the angle runs to the end of the timeline.
  const end = sourceEnd === null ? totalFrames : Math.min(totalFrames, startFrame + Math.max(0, sourceEnd - inFrame));
  const start = Math.min(startFrame, totalFrames);
  return {
    angleKey: angle.angleKey,
    name: angleDisplayName(angle),
    file,
    startFrame,
    inFrame,
    coverage: end > start ? { start, end } : null,
    lengthKnown: sourceEnd !== null,
  };
};

const uniqueSorted = (values: number[]): number[] => {
  const sorted = values.slice().sort((a, b) => a - b);
  return sorted.filter((value, index) => index === 0 || value !== sorted[index - 1]);
};

/** Index of the range containing `frame` in sorted, contiguous ranges. */
const rangeIndexAt = (ranges: FrameRange[], frame: number): number => {
  let low = 0;
  let high = ranges.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high + 1) / 2);
    if (ranges[middle].start <= frame) low = middle;
    else high = middle - 1;
  }
  return low;
};

/** Everything both exports need, in frames. Throws when roughCutProblem would report a problem. */
export const planRoughCut = (input: RoughCutInput): RoughCutPlan => {
  const rate = nleRate(input.fps);
  const sourceAngles = usableAngles(input.angles);
  if (sourceAngles.length === 0) throw new Error('The rough cut needs at least one angle.');
  const totalFrames = timelineFrames(input, sourceAngles, rate);
  if (totalFrames <= 0) throw new Error('The rough cut needs the length of the timeline.');

  const title = oneLine(input.title) || 'Untitled project';
  const width = positive(input.width) ? Math.round(input.width) : DEFAULT_WIDTH;
  const height = positive(input.height) ? Math.round(input.height) : DEFAULT_HEIGHT;

  // Files: one per distinct media, in angle order, then the recording.
  const recordingAngle = recordingAsAngle(input.audioSource);
  const names = angleFileNames(sourceAngles, input.audioSource);
  const files: PlannedFile[] = [];
  const fileByIdentity = dictionary<PlannedFile>();
  const fileOf = (angle: NleAngle): PlannedFile => {
    const identity = mediaIdentity(angle);
    if (!fileByIdentity[identity]) {
      const fileName = names[angle.angleKey];
      const file: PlannedFile = {
        id: `file-${files.length + 1}`,
        fileName,
        mediaFrames: 1,
      };
      fileByIdentity[identity] = file;
      files.push(file);
    }
    return fileByIdentity[identity];
  };

  const angles = sourceAngles.map((angle) => planAngle(angle, fileOf(angle), rate, totalFrames));
  // A file holds at least its recorded length and every frame the cut takes from it.
  sourceAngles.forEach((source, index) => {
    const planned = angles[index];
    const recorded = positive(source.durationMs) ? wholeFrames(source.durationMs, rate) : 0;
    const used = planned.coverage ? planned.inFrame + (planned.coverage.end - planned.startFrame) : planned.inFrame;
    planned.file.mediaFrames = Math.max(planned.file.mediaFrames, recorded, used, 1);
  });

  let audio: PlannedAngle | null = null;
  if (recordingAngle) {
    const identity = mediaIdentity(recordingAngle);
    const file: PlannedFile = fileByIdentity[identity] || {
      id: `file-${files.length + 1}`,
      fileName: names[AUDIO_SOURCE_KEY],
      mediaFrames: 1,
    };
    if (!fileByIdentity[identity]) {
      fileByIdentity[identity] = file;
      files.push(file);
    }
    const channels = Math.round(Number(input.audioSource?.channels));
    const sampleRate = Math.round(Number(input.audioSource?.sampleRate));
    file.audioOnly = true;
    file.channels = channels >= 1 && channels <= 64 ? channels : 2;
    file.sampleRate = sampleRate >= 8000 && sampleRate <= 384000 ? sampleRate : 48000;
    audio = { ...planAngle(recordingAngle, file, rate, totalFrames), name: oneLine(recordingName(input.audioSource), 80) };
    const recorded = positive(recordingAngle.durationMs) ? wholeFrames(recordingAngle.durationMs, rate) : 0;
    const used = audio.coverage ? audio.inFrame + (audio.coverage.end - audio.startFrame) : audio.inFrame;
    file.mediaFrames = Math.max(file.mediaFrames, recorded, used, 1);
  }

  const byKey = dictionary<PlannedAngle>();
  angles.forEach((angle) => {
    byKey[angle.angleKey] = angle;
  });
  const masterKey = byKey.A ? 'A' : angles[0].angleKey;
  const requestedAudio = String(input.audioAngleKey ?? '').trim();
  const audioKey = byKey[requestedAudio] ? requestedAudio : masterKey;
  const nameOf = (key: string) => (byKey[key] ? byKey[key].name : `Angle ${key}`);

  // The creator's cuts in time order; at the same moment the later one wins.
  const annotations = (Array.isArray(input.annotations) ? input.annotations : [])
    .map((annotation, index) => ({ annotation, index }))
    .filter(({ annotation }) => Boolean(annotation) && Number.isFinite(Number(annotation.timelineTimeMs)));
  const cuts = annotations
    .filter(({ annotation }) => isSwitch(annotation) && String(annotation.targetAngleKey ?? '').trim() !== '')
    .map(({ annotation, index }) => {
      const ms = Math.max(0, Number(annotation.timelineTimeMs));
      return { ms, index, frame: frameAt(ms, rate), key: String(annotation.targetAngleKey).trim(), text: noteText(annotation.text) };
    })
    .sort((a, b) => a.ms - b.ms || a.index - b.index);

  // What the cuts ask for: the master, then each cut's target from its frame.
  const requested: Array<FrameRange & { key: string }> = [];
  let cursor = 0;
  let requestedKey = masterKey;
  for (const cut of cuts) {
    if (cut.frame >= totalFrames) break;
    if (cut.frame > cursor) {
      requested.push({ start: cursor, end: cut.frame, key: requestedKey });
      cursor = cut.frame;
    }
    requestedKey = cut.key;
  }
  requested.push({ start: cursor, end: totalFrames, key: requestedKey });

  const covers = (angle: PlannedAngle | undefined, frame: number) =>
    Boolean(angle && angle.coverage && frame >= angle.coverage.start && frame < angle.coverage.end);
  const shownAt = (key: string, frame: number): string | null => {
    if (covers(byKey[key], frame)) return key;
    if (covers(byKey[masterKey], frame)) return masterKey;
    const any = angles.filter((angle) => covers(angle, frame))[0];
    return any ? any.angleKey : null;
  };

  // Footage only starts or stops at cuts and at angle edges, so deciding the
  // angle once per stretch between those points covers every frame.
  const points = [0, totalFrames];
  requested.forEach((range) => points.push(range.start, range.end));
  angles.forEach((angle) => {
    if (angle.coverage) points.push(angle.coverage.start, angle.coverage.end);
  });
  const edges = uniqueSorted(points.filter((point) => point >= 0 && point <= totalFrames));

  const pieces: Array<FrameRange & { requested: string; shown: string | null }> = [];
  let index = 0;
  for (let i = 0; i < edges.length - 1; i += 1) {
    const start = edges[i];
    while (requested[index].end <= start) index += 1;
    const wanted = requested[index].key;
    pieces.push({ start, end: edges[i + 1], requested: wanted, shown: shownAt(wanted, start) });
  }

  const segments: ProgramSegment[] = [];
  pieces.forEach((piece) => {
    const last = segments[segments.length - 1];
    if (last && last.angleKey === piece.shown) last.end = piece.end;
    else segments.push({ start: piece.start, end: piece.end, angleKey: piece.shown });
  });

  const markers: Array<PlannedMarker & { order: number }> = [];
  const addMarker = (marker: PlannedMarker) => markers.push({ ...marker, order: markers.length });

  // A cut's reason travels as a marker on the cut.
  cuts.forEach((cut) => {
    if (cut.frame >= totalFrames || !cut.text) return;
    addMarker({ kind: 'cut', frame: cut.frame, endFrame: null, name: oneLine(`Cut to ${nameOf(cut.key)}`), comment: cut.text });
  });

  // Where the rough cut could not show what the cut asked for, say so.
  let run: (FrameRange & { requested: string; shown: string | null }) | null = null;
  const flushRun = () => {
    if (!run || run.requested === run.shown) return;
    const missing = byKey[run.requested]
      ? `${nameOf(run.requested)} has no footage here`
      : `The cut calls for angle ${run.requested}, which isn't in this set`;
    addMarker({
      kind: 'fallback',
      frame: run.start,
      endFrame: run.end,
      name: oneLine(`No footage on ${nameOf(run.requested)}`),
      comment: run.shown
        ? `${missing}, so the rough cut shows ${nameOf(run.shown)}.`
        : `${missing}, and no other angle has footage, so the rough cut is empty here.`,
    });
  };
  pieces.forEach((piece) => {
    if (run && run.requested === piece.requested && run.shown === piece.shown) {
      run.end = piece.end;
      return;
    }
    flushRun();
    run = { ...piece };
  });
  flushRun();

  // Shorts are numbered in time order, as the editor's tasks are.
  const shortNumbers = dictionary<number>();
  annotations
    .filter(({ annotation }) => !isSwitch(annotation) && directionCategoryOf(annotation.category) === 'short')
    .sort((a, b) => Number(a.annotation.timelineTimeMs) - Number(b.annotation.timelineTimeMs) || a.index - b.index)
    .forEach(({ index }, position) => {
      shortNumbers[String(index)] = position + 1;
    });

  // The creator's notes.
  annotations.forEach(({ annotation, index }) => {
    if (isSwitch(annotation)) return;
    const startMs = Math.max(0, Number(annotation.timelineTimeMs));
    const frame = Math.min(frameAt(startMs, rate), totalFrames - 1);
    const endMs = Number(annotation.endTimeMs);
    let endFrame: number | null = null;
    if (annotation.endTimeMs !== null && annotation.endTimeMs !== undefined && Number.isFinite(endMs) && endMs > startMs) {
      const end = Math.min(frameAt(endMs, rate), totalFrames);
      endFrame = end > frame ? end : null;
    }
    const direction = directionCategoryOf(annotation.category);
    if (direction) {
      addMarker(directionMarker(direction, frame, endFrame, annotation.text, shortNumbers[String(index)] ?? 1, rate));
      return;
    }
    const target = String(annotation.targetAngleKey ?? '').trim();
    const label = getAnnotationCategory(annotation.category, annotation.color).label;
    addMarker({
      kind: 'note',
      frame,
      endFrame,
      name: oneLine(target ? `${label} (angle ${target})` : label),
      comment: noteText(annotation.text),
    });
  });

  markers.sort((a, b) => a.frame - b.frame || a.order - b.order);

  return {
    title,
    rate,
    width,
    height,
    totalFrames,
    masterKey,
    audioKey,
    audio,
    files,
    angles,
    segments,
    markers: markers.map(({ order, ...marker }) => marker),
  };
};

/**
 * A Remove or Short marked on the transcript: a range marker named for what
 * to do, the creator's words and comment in its comment, and the range
 * spelled out (Final Cut's markers are one frame long).
 */
const directionMarker = (
  kind: 'remove' | 'short',
  frame: number,
  endFrame: number | null,
  text: string | null | undefined,
  shortNumber: number,
  rate: NleRate
): PlannedMarker => {
  const { quote } = parseMarkText(String(text ?? ''));
  const words = quote ? `: “${oneLine(quote, 48)}”` : '';
  const from = framesToTimecode(frame, rate.fps);
  const to = endFrame === null ? null : framesToTimecode(endFrame, rate.fps);
  let how: string;
  if (kind === 'remove') {
    how = to ? `Take ${from} to ${to} out of the edit.` : `Take out the words at ${from}; ask the creator where it ends.`;
  } else {
    how = to
      ? `Make ${from} to ${to} into short ${shortNumber}: vertical, captioned, the hook first.`
      : `Make short ${shortNumber} from ${from}; ask the creator where it ends.`;
  }
  return {
    kind,
    frame,
    endFrame,
    name: oneLine(kind === 'remove' ? `Remove${words}` : `Short ${shortNumber}${words}`),
    comment: [noteText(text), how].filter(Boolean).join('\n'),
  };
};

// ---------------------------------------------------------------------------
// XML writing
// ---------------------------------------------------------------------------

type Attributes = Array<[string, string | number | null | undefined]>;

const attributeText = (attributes?: Attributes): string =>
  (attributes || [])
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([name, value]) => ` ${name}="${escapeAttribute(value)}"`)
    .join('');

/** Indented XML, written line by line (fast enough for a three-hour set with thousands of cuts). */
class XmlWriter {
  private readonly lines: string[];
  private depth = 0;

  constructor(prolog: string[]) {
    this.lines = prolog.slice();
  }

  private indent(): string {
    return '  '.repeat(this.depth);
  }

  open(name: string, attributes?: Attributes): void {
    this.lines.push(`${this.indent()}<${name}${attributeText(attributes)}>`);
    this.depth += 1;
  }

  close(name: string): void {
    this.depth -= 1;
    this.lines.push(`${this.indent()}</${name}>`);
  }

  text(name: string, value: string | number, attributes?: Attributes): void {
    this.lines.push(`${this.indent()}<${name}${attributeText(attributes)}>${escapeText(value)}</${name}>`);
  }

  empty(name: string, attributes?: Attributes): void {
    this.lines.push(`${this.indent()}<${name}${attributeText(attributes)}/>`);
  }

  toString(): string {
    return `${this.lines.join('\n')}\n`;
  }
}

const XML_DECLARATION = '<?xml version="1.0" encoding="UTF-8"?>';

const sourceFrameAt = (angle: PlannedAngle, timelineFrame: number) => angle.inFrame + (timelineFrame - angle.startFrame);

const angleByKey = (plan: RoughCutPlan, key: string): PlannedAngle => {
  const angle = plan.angles.filter((candidate) => candidate.angleKey === key)[0];
  if (!angle) throw new Error(`Unknown angle ${key}`);
  return angle;
};

/** Markers grouped by the program segment that contains them. */
const markersBySegment = (plan: RoughCutPlan): PlannedMarker[][] => {
  const groups: PlannedMarker[][] = plan.segments.map(() => []);
  plan.markers.forEach((marker) => {
    groups[rangeIndexAt(plan.segments, marker.frame)].push(marker);
  });
  return groups;
};

// ---------------------------------------------------------------------------
// FCP7 XML (xmeml) for Premiere Pro and DaVinci Resolve
// ---------------------------------------------------------------------------

/** Final Cut Pro 7's named marker colours, as RGB: the creator's directions stand out from the notes. */
const XMEML_MARKER_COLORS: Partial<Record<MarkerKind, [number, number, number]>> = {
  remove: [255, 0, 0],
  short: [255, 0, 127],
};

/** A file:// URL for a bare file name (Apple's xmeml reference requires file://localhost or file:///). */
const fileUrl = (fileName: string) => `file://localhost/${encodeURIComponent(fileName)}`;

/**
 * FCP7 XML ("xmeml" version 4, the version Premiere Pro writes; Resolve reads
 * it too):
 *  - V1: the creator's cut, one clipitem per stretch of one angle;
 *  - V2 onwards: each angle's footage in sync for the whole cut, one angle per
 *    track, in key order, disabled;
 *  - A1 and A2: the sound's two channels (stereo, panned left and right; a
 *    mono recording on both) for as long as it has sound: the separate
 *    recording, an audio-only file, or else the sound angle;
 *  - the creator's notes as sequence markers.
 * Each media file is defined once, at its first use, then referenced by id.
 */
export const buildPremiereXml = (input: RoughCutInput): string => {
  const plan = planRoughCut(input);
  const { rate } = plan;
  const xml = new XmlWriter([XML_DECLARATION, '<!DOCTYPE xmeml>']);

  const writeRate = () => {
    xml.open('rate');
    xml.text('timebase', rate.timebase);
    xml.text('ntsc', rate.ntsc ? 'TRUE' : 'FALSE');
    xml.close('rate');
  };
  const writeTimecode = () => {
    xml.open('timecode');
    writeRate();
    xml.text('string', framesToTimecode(0, rate.fps));
    xml.text('frame', 0);
    xml.text('displayformat', rate.dropFrame ? 'DF' : 'NDF');
    xml.close('timecode');
  };
  const writeVideoCharacteristics = () => {
    xml.open('samplecharacteristics');
    writeRate();
    xml.text('width', plan.width);
    xml.text('height', plan.height);
    xml.text('anamorphic', 'FALSE');
    xml.text('pixelaspectratio', 'square');
    xml.text('fielddominance', 'none');
    xml.close('samplecharacteristics');
  };
  const writeAudioCharacteristics = (sampleRate = 48000) => {
    xml.open('samplecharacteristics');
    xml.text('depth', 16);
    xml.text('samplerate', sampleRate);
    xml.close('samplecharacteristics');
  };

  const defined = dictionary<boolean>();
  const writeFile = (file: PlannedFile) => {
    if (defined[file.id]) {
      xml.empty('file', [['id', file.id]]);
      return;
    }
    defined[file.id] = true;
    xml.open('file', [['id', file.id]]);
    // The file's own name, the same as in pathurl: an importer that matches on
    // either finds the same file, never another camera's file of the same name.
    xml.text('name', file.fileName);
    xml.text('pathurl', fileUrl(file.fileName));
    writeRate();
    xml.text('duration', file.mediaFrames);
    // In and out points count from the file's first frame, labelled 00:00:00:00.
    writeTimecode();
    xml.open('media');
    if (!file.audioOnly) {
      xml.open('video');
      writeVideoCharacteristics();
      xml.close('video');
    }
    xml.open('audio');
    writeAudioCharacteristics(file.sampleRate);
    xml.text('channelcount', file.channels ?? 2);
    xml.close('audio');
    xml.close('media');
    xml.close('file');
  };

  let clipCount = 0;
  const nextClipId = () => {
    clipCount += 1;
    return `clipitem-${clipCount}`;
  };
  const writeClipitem = (options: {
    id: string;
    angle: PlannedAngle;
    range: FrameRange;
    enabled: boolean;
    audioChannel?: number;
    links?: Array<{ id: string; track: number }>;
  }) => {
    const { angle, range } = options;
    const sourceIn = sourceFrameAt(angle, range.start);
    xml.open('clipitem', [['id', options.id]]);
    // One master clip per file, so the bin holds each file once.
    xml.text('masterclipid', angle.file.id.replace(/^file-/, 'masterclip-'));
    xml.text('name', angle.name);
    xml.text('enabled', options.enabled ? 'TRUE' : 'FALSE');
    xml.text('duration', angle.file.mediaFrames);
    writeRate();
    xml.text('start', range.start);
    xml.text('end', range.end);
    xml.text('in', sourceIn);
    xml.text('out', sourceIn + (range.end - range.start));
    writeFile(angle.file);
    if (options.audioChannel) {
      xml.open('sourcetrack');
      xml.text('mediatype', 'audio');
      xml.text('trackindex', options.audioChannel);
      xml.close('sourcetrack');
    }
    (options.links || []).forEach((link) => {
      xml.open('link');
      xml.text('linkclipref', link.id);
      xml.text('mediatype', 'audio');
      xml.text('trackindex', link.track);
      xml.text('clipindex', 1);
      xml.text('groupindex', 1);
      xml.close('link');
    });
    xml.close('clipitem');
  };
  const closeTrack = (outputChannel?: number) => {
    xml.text('enabled', 'TRUE');
    xml.text('locked', 'FALSE');
    if (outputChannel) xml.text('outputchannelindex', outputChannel);
    xml.close('track');
  };

  xml.open('xmeml', [['version', 4]]);
  xml.open('sequence', [['id', 'sequence-1']]);
  xml.text('duration', plan.totalFrames);
  writeRate();
  xml.text('name', `${plan.title} (rough cut)`);
  xml.open('media');

  xml.open('video');
  xml.open('format');
  writeVideoCharacteristics();
  xml.close('format');

  // V1: the creator's cut. Stretches where no angle has footage stay empty.
  xml.open('track');
  plan.segments.forEach((segment) => {
    if (segment.angleKey === null) return;
    writeClipitem({ id: nextClipId(), angle: angleByKey(plan, segment.angleKey), range: segment, enabled: true });
  });
  closeTrack();

  // V2 onwards: every angle in sync for the whole cut, disabled. A disabled
  // clip neither plays nor covers V1, yet it is there to enable, to cut to by
  // hand, or to select with the others to build a multi-camera source sequence.
  plan.angles.forEach((angle) => {
    xml.open('track');
    if (angle.coverage) writeClipitem({ id: nextClipId(), angle, range: angle.coverage, enabled: false });
    closeTrack();
  });
  xml.close('video');

  xml.open('audio');
  xml.text('numOutputChannels', 2);
  xml.open('format');
  writeAudioCharacteristics();
  xml.close('format');
  xml.open('outputs');
  [1, 2].forEach((channel) => {
    xml.open('group');
    xml.text('index', channel);
    xml.text('numchannels', 1);
    xml.text('downmix', 0);
    xml.open('channel');
    xml.text('index', channel);
    xml.close('channel');
    xml.close('group');
  });
  xml.close('outputs');

  // A1/A2: the sound's left and right channels (the recording's, or the
  // sound angle's), linked as one stereo pair, running unbroken under the
  // cuts. A mono recording's one channel goes on both.
  const sound = plan.audio ?? angleByKey(plan, plan.audioKey);
  const mono = (sound.file.channels ?? 2) === 1;
  const leftId = nextClipId();
  const rightId = nextClipId();
  const links = [{ id: leftId, track: 1 }, { id: rightId, track: 2 }];
  [leftId, rightId].forEach((id, channelIndex) => {
    xml.open('track');
    if (sound.coverage) {
      writeClipitem({ id, angle: sound, range: sound.coverage, enabled: true, audioChannel: mono ? 1 : channelIndex + 1, links });
    }
    closeTrack(channelIndex + 1);
  });
  xml.close('audio');

  xml.close('media');
  writeTimecode();

  plan.markers.forEach((marker) => {
    xml.open('marker');
    xml.text('comment', marker.comment);
    xml.text('name', marker.name);
    xml.text('in', marker.frame);
    // -1: a point marker, as Premiere writes it.
    xml.text('out', marker.endFrame === null ? -1 : marker.endFrame);
    // Final Cut Pro 7's marker colours (alpha is always written as 0): Remove red, Short pink.
    const color = XMEML_MARKER_COLORS[marker.kind];
    if (color) {
      xml.open('color');
      xml.text('alpha', 0);
      xml.text('red', color[0]);
      xml.text('green', color[1]);
      xml.text('blue', color[2]);
      xml.close('color');
    }
    xml.close('marker');
  });

  xml.close('sequence');
  xml.close('xmeml');
  return xml.toString();
};

// ---------------------------------------------------------------------------
// FCPXML for Final Cut Pro (and DaVinci Resolve)
// ---------------------------------------------------------------------------

/**
 * FCPXML 1.10: Final Cut Pro 10.6 and later import it, and so does DaVinci
 * Resolve 18 and later. It has everything this export uses (media-rep came in
 * 1.9, mc-source angle ids in 1.2); later versions need newer apps and add
 * nothing it needs.
 */
export const FCPXML_VERSION = '1.10';

/** A whole number of frames as an FCPXML time: "0s", "5s", "1001/30000s". */
export const fcpxmlTime = (frames: number, rate: NleRate): string => {
  const numerator = Math.max(0, Math.round(frames)) * rate.frameNumerator;
  if (numerator === 0) return '0s';
  return numerator % rate.frameDenominator === 0
    ? `${numerator / rate.frameDenominator}s`
    : `${numerator}/${rate.frameDenominator}s`;
};

/** Final Cut's names for 1080p formats; other sizes and rates are described by their attributes alone. */
const fcpFormatName = (plan: RoughCutPlan): string | null => {
  if (plan.width !== 1920 || plan.height !== 1080) return null;
  const { timebase, ntsc } = plan.rate;
  const suffixes: Record<string, string> = { '24n': '2398', '24': '24', '25': '25', '30n': '2997', '30': '30', '50': '50', '60n': '5994', '60': '60' };
  const suffix = suffixes[`${timebase}${ntsc ? 'n' : ''}`];
  return suffix ? `FFVideoFormat1080p${suffix}` : null;
};

const angleId = (key: string) => `pithplay-angle-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`;
/** The separate recording's angle in the multicam clip. */
const RECORDING_ANGLE_ID = 'pithplay-separate-audio';

const baseName = (fileName: string) => fileName.slice(0, fileName.length - extensionOf(fileName).length) || fileName;

/**
 * FCPXML 1.10 with a real multicam clip:
 *  - one <asset> per file, its media-rep pointing at ./<file name> (next to
 *    the .fcpxml);
 *  - a multicam <media> with one <mc-angle> per angle, each angle's clip
 *    placed at its sync offset after a gap;
 *  - a project whose spine is one <mc-clip> per stretch of the creator's cut,
 *    taking video from that stretch's angle and sound from the audio angle,
 *    so the editor can still switch angles in the angle viewer;
 *  - a separate recording as an audio-only asset and one more angle of the
 *    multicam clip, which every <mc-clip> takes its sound from;
 *  - the notes as markers on the clip they fall in.
 */
export const buildFcpxml = (input: RoughCutInput): string => {
  const plan = planRoughCut(input);
  const { rate } = plan;
  const time = (frames: number) => fcpxmlTime(frames, rate);
  const tcFormat = rate.dropFrame ? 'DF' : 'NDF';
  const xml = new XmlWriter([XML_DECLARATION, '<!DOCTYPE fcpxml>']);

  const assetIds = dictionary<string>();
  plan.files.forEach((file, index) => {
    assetIds[file.id] = `r${index + 2}`;
  });
  const multicamId = `r${plan.files.length + 2}`;
  const multicamName = `${plan.title} (multicam)`;

  xml.open('fcpxml', [['version', FCPXML_VERSION]]);
  xml.open('resources');
  xml.empty('format', [
    ['id', 'r1'],
    ['name', fcpFormatName(plan)],
    ['frameDuration', `${rate.frameNumerator}/${rate.frameDenominator}s`],
    ['width', plan.width],
    ['height', plan.height],
  ]);
  plan.files.forEach((file) => {
    // Apple recommends stating these for media that may be offline at import.
    // A camera's channel count and sample rate are not recorded, so stereo at
    // 48 kHz; a recording's were read at upload. A recording has no picture.
    xml.open('asset', [
      ['id', assetIds[file.id]],
      ['name', baseName(file.fileName)],
      ['start', '0s'],
      ['duration', time(file.mediaFrames)],
      ['hasVideo', file.audioOnly ? null : 1],
      ['format', file.audioOnly ? null : 'r1'],
      ['hasAudio', 1],
      ['audioSources', 1],
      ['audioChannels', file.channels ?? 2],
      ['audioRate', file.sampleRate ?? 48000],
    ]);
    xml.empty('media-rep', [['kind', 'original-media'], ['src', `./${encodeURIComponent(file.fileName)}`]]);
    xml.close('asset');
  });

  xml.open('media', [['id', multicamId], ['name', multicamName]]);
  xml.open('multicam', [['format', 'r1'], ['tcStart', '0s'], ['tcFormat', tcFormat]]);
  const writeMcAngle = (angle: PlannedAngle, id: string) => {
    xml.open('mc-angle', [['name', angle.name], ['angleID', id]]);
    const coverage = angle.coverage;
    if (!coverage) {
      xml.empty('gap', [['name', 'Gap'], ['offset', '0s'], ['duration', time(plan.totalFrames)]]);
    } else {
      if (coverage.start > 0) xml.empty('gap', [['name', 'Gap'], ['offset', '0s'], ['duration', time(coverage.start)]]);
      xml.empty('asset-clip', [
        ['ref', assetIds[angle.file.id]],
        ['offset', time(coverage.start)],
        ['name', baseName(angle.file.fileName)],
        ['start', time(sourceFrameAt(angle, coverage.start))],
        ['duration', time(coverage.end - coverage.start)],
        ['audioRole', angle.file.audioOnly ? 'dialogue' : null],
      ]);
    }
    xml.close('mc-angle');
  };
  plan.angles.forEach((angle) => writeMcAngle(angle, angleId(angle.angleKey)));
  if (plan.audio) writeMcAngle(plan.audio, RECORDING_ANGLE_ID);
  xml.close('multicam');
  xml.close('media');
  xml.close('resources');

  const writeMarker = (marker: PlannedMarker) => {
    const direction = marker.kind === 'remove' || marker.kind === 'short';
    const until = marker.endFrame === null || direction ? '' : `Until ${framesToTimecode(marker.endFrame, rate.fps)}.`;
    const note = [marker.comment, until].filter(Boolean).join('\n');
    // Final Cut markers are one frame long; a range's end is in the note. A
    // Remove is a to-do marker (red until the editor ticks it off); a Short
    // stays a standard marker, since it is made outside this timeline.
    xml.empty('marker', [
      ['start', time(marker.frame)],
      ['duration', time(1)],
      ['value', marker.name],
      ['completed', marker.kind === 'remove' ? '0' : null],
      ['note', note || null],
    ]);
  };

  // library > event > project, the way Final Cut and Resolve write their own
  // exports. With no location, Final Cut imports into the library the editor picks.
  xml.open('library');
  xml.open('event', [['name', plan.title]]);
  xml.open('project', [['name', `${plan.title} (rough cut)`]]);
  xml.open('sequence', [
    ['format', 'r1'],
    ['duration', time(plan.totalFrames)],
    ['tcStart', '0s'],
    ['tcFormat', tcFormat],
    ['audioLayout', 'stereo'],
    ['audioRate', '48k'],
  ]);
  xml.open('spine');
  const groups = markersBySegment(plan);
  plan.segments.forEach((segment, index) => {
    // The multicam's clock is the timeline's, so a clip's start (where it
    // reads the multicam from) equals its offset, and markers keep timeline time.
    const timing: Attributes = [
      ['offset', time(segment.start)],
      ['start', time(segment.start)],
      ['duration', time(segment.end - segment.start)],
    ];
    if (segment.angleKey === null) {
      xml.open('gap', [['name', 'Gap'], ...timing]);
      groups[index].forEach(writeMarker);
      xml.close('gap');
      return;
    }
    xml.open('mc-clip', [['ref', multicamId], ['offset', time(segment.start)], ['name', multicamName], ['start', time(segment.start)], ['duration', time(segment.end - segment.start)]]);
    if (!plan.audio && segment.angleKey === plan.audioKey) {
      xml.empty('mc-source', [['angleID', angleId(segment.angleKey)], ['srcEnable', 'all']]);
    } else {
      xml.empty('mc-source', [['angleID', angleId(segment.angleKey)], ['srcEnable', 'video']]);
      xml.empty('mc-source', [['angleID', plan.audio ? RECORDING_ANGLE_ID : angleId(plan.audioKey)], ['srcEnable', 'audio']]);
    }
    groups[index].forEach(writeMarker);
    xml.close('mc-clip');
  });
  xml.close('spine');
  xml.close('sequence');
  xml.close('project');
  xml.close('event');
  xml.close('library');
  xml.close('fcpxml');
  return xml.toString();
};

// ---------------------------------------------------------------------------
// Downloads
// ---------------------------------------------------------------------------

export type RoughCutFormat = 'premiere' | 'fcpxml';

export const ROUGH_CUT_FORMATS: Array<{ format: RoughCutFormat; label: string; description: string }> = [
  {
    format: 'premiere',
    label: 'Premiere Pro / DaVinci Resolve (.xml)',
    description: 'The cut on V1, each angle in sync on its own track',
  },
  {
    format: 'fcpxml',
    label: 'Final Cut Pro (.fcpxml)',
    description: 'A multicam clip, switched where the creator cut',
  },
];

/** File name, MIME type and content for a rough cut download. */
export const buildRoughCutExport = (
  format: RoughCutFormat,
  input: RoughCutInput
): { filename: string; mimeType: string; content: string } => {
  const base = safeFileName(input.title, 'multicam');
  return format === 'fcpxml'
    ? { filename: `${base}-rough-cut.fcpxml`, mimeType: 'application/xml;charset=utf-8', content: buildFcpxml(input) }
    : { filename: `${base}-rough-cut.xml`, mimeType: 'application/xml;charset=utf-8', content: buildPremiereXml(input) };
};
